import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPhone, 
  faEnvelope, 
  faMapMarkerAlt,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { 
  faFacebookF,
  faTwitter,
  faLinkedinIn,
  faInstagram,
  faYoutube
} from '@fortawesome/free-brands-svg-icons';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Social media links data
  const socialLinks = [
    { icon: faFacebookF, url: "https://www.facebook.com/profile.php?id=61579266181929", label: "Facebook" },
    { icon: faTwitter, url: "#", label: "Twitter" },
    { icon: faLinkedinIn, url: "https://www.linkedin.com/in/corenova-renewable-energies/", label: "LinkedIn" },
    { icon: faInstagram, url: "https://www.instagram.com/corenova.re/", label: "Instagram" },
    { icon: faYoutube, url: "#", label: "YouTube" }
  ];

  // Quick links data
  const quickLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About Us" },
    { path: "/services", label: "Services" },
    { path: "/contact", label: "Contact" },
    { path: "#projects", label: "Projects" },
    { path: "/blog", label: "Blog" }
  ];

  // Services links data
  const servicesLinks = [
    { path: "#solar", label: "Solar Power Solutions" },
    { path: "#wind", label: "Wind Energy Systems" },
    { path: "#hydro", label: "Hydroelectric Power" },
    { path: "#storage", label: "Energy Storage" },
    { path: "#consulting", label: "Consulting" },
    { path: "#maintenance", label: "Maintenance" }
  ];

  // Policy links data
  const policyLinks = [
    { path: "#privacy", label: "Privacy Policy" },
    { path: "#terms", label: "Terms of Service" },
    { path: "#cookies", label: "Cookie Policy" }
  ];

  return (
    <footer className="bg-dark text-white mt-auto">
      {/* Main Footer Content */}
      <div className="py-16 lg:py-20">
        <div className="container">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            
            {/* Company Information */}
            <div className="space-y-6">
              <div className="flex justify-center md:justify-start">
                <img 
                  src="/images/renewable.png" 
                  alt="Corenova Logo" 
                  className="h-20 w-auto object-contain md:h-24 lg:h-28" 
                />
              </div>
              
              <p className="text-gray-300 leading-relaxed text-lg">
                Leading the renewable energy revolution with innovative solutions and sustainable practices. 
                We are committed to creating a cleaner, greener future for generations to come.
              </p>
              
              {/* Social Media Links */}
              <div className="flex gap-3">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-gradient-to-r hover:from-primary-green hover:to-secondary-blue hover:-translate-y-1 transition-all duration-300"
                    aria-label={social.label}
                  >
                    <FontAwesomeIcon icon={social.icon} className="text-sm" />
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links Section */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-white relative pb-2">
                Quick Links
                <span className="absolute bottom-0 left-0 w-8 h-0.5 bg-gradient-to-r from-primary-green to-secondary-blue"></span>
              </h3>
              
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <Link 
                      to={link.path} 
                      className="text-gray-300 hover:text-primary-green hover:translate-x-1 transition-all duration-300 inline-flex items-center gap-2 group"
                    >
                      <FontAwesomeIcon 
                        icon={faArrowRight} 
                        className="text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300" 
                      />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services Section */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-white relative pb-2">
                Our Services
                <span className="absolute bottom-0 left-0 w-8 h-0.5 bg-gradient-to-r from-primary-green to-secondary-blue"></span>
              </h3>
              
              <ul className="space-y-3">
                {servicesLinks.map((service, index) => (
                  <li key={index}>
                    <a 
                      href={service.path} 
                      className="text-gray-300 hover:text-primary-green hover:translate-x-1 transition-all duration-300 inline-flex items-center gap-2 group"
                    >
                      <FontAwesomeIcon 
                        icon={faArrowRight} 
                        className="text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300" 
                      />
                      {service.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-white relative pb-2">
                Contact Info
                <span className="absolute bottom-0 left-0 w-8 h-0.5 bg-gradient-to-r from-primary-green to-secondary-blue"></span>
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <FontAwesomeIcon 
                    icon={faMapMarkerAlt} 
                    className="text-primary-green text-lg mt-1 flex-shrink-0" 
                  />
                  <div className="text-gray-300 leading-relaxed">
                    Plot no -109, Old Ag Colony, Unit 4, Madhusudan Nagar, Bhubaneswar, Odisha 751001
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <FontAwesomeIcon 
                    icon={faPhone} 
                    className="text-primary-green text-lg flex-shrink-0" 
                  />
                  <a 
                    href="tel:+918093237123" 
                    className="text-gray-300 hover:text-primary-green transition-colors duration-300"
                  >
                    +91 8093237123
                  </a>
                </div>
                
                <div className="flex items-center gap-4">
                  <FontAwesomeIcon 
                    icon={faEnvelope} 
                    className="text-primary-green text-lg flex-shrink-0" 
                  />
                  <a 
                    href="mailto:corenovarenewable@gmail.com" 
                    className="text-gray-300 hover:text-primary-green transition-colors duration-300"
                  >
                    corenovarenewable@gmail.com
                  </a>
                </div>

                {/* Google Map Embed */}
                <div className="pt-4">
                  <div className="rounded-lg overflow-hidden shadow-lg border-2 border-primary-green/30">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14968.142432383795!2d85.815919!3d20.296059!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjDCsDE3JzQ1LjgiTiA4NcKwNDgnNTcuMyJF!5e0!3m2!1sen!2sin!4v1620000000000!5m2!1sen!2sin"
                      width="100%"
                      height="76"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Corenova Renewable Energy Location"
                      className="w-full"
                    />
                  </div>
                  
                  {/* Map Link */}
                  <div className="text-center mt-2">
                    <a
                      href="https://maps.google.com/?q=Plot+no+-109,+Old+Ag+Colony,+Unit+4,+Madhusudan+Nagar,+Bhubaneswar,+Odisha+751001"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary-green hover:text-secondary-blue text-xs transition-colors duration-300"
                    >
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="text-xs" />
                      Open in Google Maps
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Bottom Bar */}
      <div className="bg-black/30 border-t border-white/10 py-6">
        <div className="container">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © {currentYear} Corenova Agency Pvt Ltd. All rights reserved.
            </p>
            
            <div className="flex gap-6 text-sm">
              {policyLinks.map((policy, index) => (
                <a
                  key={index}
                  href={policy.path}
                  className="text-gray-400 hover:text-primary-green transition-colors duration-300"
                >
                  {policy.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;